<template>
  <div class="card-box" :class="{ outlookAddin }">
    <div class="powered" v-if="logoLoginUrl">
      <img src="~assets/img/qreserve.svg" alt="Powered by QReserve" />
    </div>
    <div class="form-group">
      <!-- Logo, subtitle, and sign up text -->
      <h1 class="logo fade-in-load-logo" aria-label="Login">
        <img
          v-if="logoLoginUrl"
          :style="logoStyle"
          :src="logoLoginUrl"
          :alt="themeName" />
        <img
          v-if="!logoLoginUrl && !darkMode"
          src="~assets/img/qreserve.svg"
          alt="QReserve" />
        <img
          v-if="!logoLoginUrl && darkMode"
          src="~assets/img/qreserve-white.svg"
          alt="QReserve" />
      </h1>

      <div class="infobox infobox-error infobox-full" v-if="logoutMessage">
        {{ logoutMessage }}
      </div>

      <div class="infobox infobox-info infobox-full" v-if="topMessage">
        {{ topMessage }}
      </div>

      <div class="infobox infobox-info infobox-full" v-if="outlookAddin">
        Easily reserve QReserve resources for your Outlook calendar events.
        <!-- eslint-disable -->
        <a
          href="https://get.qreserve.com/documentation/outlook-add-in"
          class="inline"
          target="_blank">
          Learn More
        </a>
        <!-- eslint-enable -->
      </div>

      <div class="infobox infobox-info infobox-full" v-if="chromeExtension">
        Easily reserve QReserve resources for your Google calendar events.
        <!-- eslint-disable -->
        <a
          href="https://get.qreserve.com/documentation/google-calendar-browser-extension"
          class="inline"
          target="_blank">
          Learn More
        </a>
        <!-- eslint-enable -->
      </div>

      <!-- Username and Password Entry -->
      <div class="infobox infobox-info infobox-full" v-if="isOneTimeLoggingIn">
        Logging in...
      </div>

      <!-- Main login panel with username and password -->
      <div class="fade-in-load-login" v-if="!isOneTimeLoggingIn && !mfaMode">
        <div
          class="login-form default-sso"
          v-if="defaultSSOMode || emailProvidedToSSO">
          <div style="font-size: larger" v-if="!emailProvidedToSSO">
            <span>Sign in again with</span>
            <div
              v-if="defaultSSO.logos && defaultSSO.logos.length > 0"
              class="logo">
              <img :src="defaultSSO.logos[0]" :alt="defaultSSO.names[0]" />
            </div>
            <span class="name">{{ defaultSSO.names[0] }}</span>
            <button
              style="margin-top: 1em; font-size: 1.1em"
              class="btn btn-primary card-button"
              @click="$emit('emitLoginWithDefaultSSO', defaultSSO)">
              Sign in
              <template v-if="themeName">
                to
                <strong>{{ themeName }}</strong>
              </template>
              with partner
            </button>
          </div>
          <div v-else>
            <span>
              After verifying your email, please click the sign-in button below
              to sign into your account.
            </span>
            <br />
            <span class="name">{{ emailProvidedToSSO.name }}</span>
            <span>{{ emailProvidedToSSO.email }}</span>
            <button
              style="font-size: 1.1em"
              class="btn btn-primary btn-default-sso"
              @click="$emit('emitLoginWithDefaultSSO', emailProvidedToSSO)">
              Sign in with partner
            </button>
          </div>
        </div>

        <!-- If emailOverride is set, it means that a URL parameter has
          overriden the email and it's intended for the user to log in
          with this email and not with a SSO partner. In the case that
          there is a default SSO set from a first time login with an
          SSO and the user already has an account, make sure to make it
          not a clickable link and just display the name of the SSO so
          that the user doesn't try to log in through them again until
          properly associating their account. -->
        <template
          v-if="
            (defaultSSO && emailOverride) ||
            (emailOverride && $route.query.idp_membership)
          ">
          <div class="infobox infobox-info infobox-full">
            We need you to sign in to your existing QReserve account to
            associate it with
            <strong>
              {{
                lodash.get(defaultSSO, 'names[0]') ||
                $route.query.idp_entity_name
              }}
            </strong>
          </div>
        </template>

        <div class="login-form" v-if="!defaultSSOMode && !emailProvidedToSSO">
          <div
            class="form-group"
            style="margin-top: 0.5em"
            :class="{
              'has-error': error || (loginInfoEntered && !validEmail),
            }">
            <label class="form-label" for="email-address">E-mail address</label>
            <span class="icon form-icon">
              <font-awesome-icon :icon="faIcons.faEnvelope" />
            </span>
            <input
              class="form-input form-icon-offset"
              type="email"
              id="email-address"
              v-model="credentials.email"
              :disabled="Boolean(emailOverride) || null"
              @keydown.enter="
                () => {
                  if (entityIdForEmailDomain) {
                    $emit('emitLoginWithDefaultSSO', {
                      entity_id: entityIdForEmailDomain,
                    });
                  }
                  doLogin();
                }
              "
              v-focus />
            <transition name="fade">
              <p
                aria-live="assertive"
                class="form-input-hint"
                v-show="loginInfoEntered && !validEmail">
                This is not a valid email address.
              </p>
            </transition>
          </div>

          <div
            class="form-group"
            :class="{ 'has-error': error }"
            style="margin-top: 1em"
            v-show="!entityIdForEmailDomain">
            <button
              @click="goToPasswordReset()"
              id="forgot-password"
              class="btn btn-link above-input-message">
              Forgot your password?
            </button>
            <label class="form-label" for="password">Password</label>
            <span class="icon form-icon">
              <font-awesome-icon :icon="faIcons.faLock" />
            </span>
            <input
              class="form-input form-icon-offset"
              type="password"
              autocomplete="current-password"
              id="password"
              maxlength="8192"
              v-model="credentials.password"
              @keydown.enter="doLogin()" />
            <transition name="fade">
              <div v-if="error && !errorIsVerification">
                <p aria-live="assertive" class="form-input-hint">
                  {{ formattedError }}
                </p>
              </div>
            </transition>
            <transition name="fade">
              <div v-if="error && errorIsVerification">
                <p aria-live="assertive" class="form-input-hint">
                  Email verification required for this account.
                </p>
              </div>
            </transition>
          </div>
        </div>

        <!-- Login/Forgot Password Buttons -->
        <div
          :class="{
            'button-box': !defaultSSOMode,
          }">
          <button
            class="btn btn-primary card-button"
            id="sign-in"
            :class="{ loading: isLoading }"
            @click="doLogin()"
            v-if="
              !defaultSSOMode && !emailProvidedToSSO && !entityIdForEmailDomain
            "
            :disabled="isLoading || !loginInfoEntered || !validEmail || null">
            Sign In
            <template v-if="themeName">
              to
              <strong>{{ themeName }}</strong>
            </template>
          </button>
          <button
            v-if="
              !defaultSSOMode && !emailProvidedToSSO && entityIdForEmailDomain
            "
            class="btn btn-primary card-button"
            id="sign-in"
            :class="{ loading: isLoading }"
            @click="
              $emit('emitLoginWithDefaultSSO', {
                entity_id: entityIdForEmailDomain,
              })
            "
            :disabled="isLoading || !validEmail || null">
            Sign In At {{ credentials.email.split('@').pop() }}
            <template v-if="themeName">
              to
              <strong>{{ themeName }}</strong>
            </template>
          </button>
          <div v-if="themeName" class="theme-banner">
            <button class="btn btn-link inline" @click="$emit('clearTheme')">
              <strong>Wrong location?</strong>
              Return to QReserve
            </button>
          </div>

          <div style="margin: 1.25em 0">
            <template
              v-if="
                !defaultSSOMode &&
                !emailOverride &&
                !emailProvidedToSSO &&
                !hidePartnerLogin
              ">
              <button
                id="partner-login-link"
                class="btn btn-link"
                style="float: right; font-size: 1.1em"
                @click="$emit('emitShowMembershipPortal')">
                <font-awesome-icon :icon="faIcons.faHandshakeAltSolid" />
                Sign in with partner
              </button>
            </template>

            <div v-if="!QReserveReadOnlyMode && !emailProvidedToSSO">
              <label class="form-checkbox remember-me-switch">
                <input type="checkbox" v-model="saveSession" />
                <i class="form-icon"></i>
                Remember me
              </label>
              <hr style="margin: 2em 0 1.5em 0" />
              <div
                v-if="
                  !QReserveStandaloneInstall &&
                  !entityIdForEmailDomain &&
                  !defaultSSOMode &&
                  !emailProvidedToSSO &&
                  !defaultSSO
                "
                class="sign-up-block">
                Don't have an account yet?
                <br />
                <button
                  class="btn btn-link inline"
                  style="font-size: larger"
                  id="create-account"
                  @click="goToSignup()">
                  Create your new account
                </button>
              </div>
              <div
                v-if="
                  !hidePartnerLogin &&
                  !QReserveReadOnlyMode &&
                  !entityIdForEmailDomain
                ">
                <template
                  v-if="
                    (defaultSSOMode || emailProvidedToSSO) && !emailOverride
                  ">
                  <div class="sign-up-block">
                    Want to use a regular QReserve account?
                    <br />
                    <button
                      class="btn btn-link inline"
                      style="font-size: larger"
                      @click="resetDefaultSSO">
                      Sign in here
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TOTP Mode -->
      <div
        class="fade-in-load-login"
        v-if="!isOneTimeLoggingIn && mfaMode === MFA_TYPES.TOTP">
        <div
          class="form-group"
          style="margin-top: 0.5em"
          :class="{
            'has-error': error || (loginInfoEntered && !validEmail),
          }">
          <div v-show="!showRecoveryCodeInput">
            <label class="form-label" for="totp-code">
              Please confirm your account by entering the code generated by your
              authenticator app.
            </label>
            <span class="icon form-icon-totp">
              <font-awesome-icon :icon="faIcons.faKeySolid" />
            </span>
            <input
              class="form-input totp-input-box"
              id="totp-code"
              autocomplete="one-time-code"
              inputmode="numeric"
              maxlength="7"
              v-model="credentials.totpCode"
              @keydown.enter="doLogin"
              v-focus />
          </div>
          <br />
          <button
            v-show="!showRecoveryCodeInput"
            class="btn btn-link inline"
            @click="showRecoveryCodeInput = true">
            Use optional recovery code
          </button>
          <div v-show="showRecoveryCodeInput">
            <label class="form-label" for="recovery-code">Recovery Code</label>
            <input
              class="form-input"
              id="recovery-code"
              v-model="credentials.totpRecoveryCode"
              @keydown.enter="doLogin"
              v-focus />
          </div>
          <transition name="fade">
            <div v-if="error">
              <p aria-live="assertive" class="form-input-hint">
                {{ formattedError }}
              </p>
            </div>
          </transition>
        </div>
        <div
          :class="{
            'button-box': !defaultSSOMode,
          }">
          <button
            class="btn btn-primary card-button"
            id="sign-in"
            :class="{ loading: isLoading }"
            @click="doLogin()"
            :disabled="
              isLoading ||
              (credentials.totpCode.length < 6 && !credentials.totpRecoveryCode)
            ">
            Sign In
          </button>
        </div>
        <div
          style="margin-top: 1em"
          v-if="!QReserveReadOnlyMode && !emailProvidedToSSO">
          <label class="form-checkbox remember-me-switch">
            <input type="checkbox" v-model="saveSession" />
            <i class="form-icon"></i>
            Remember me
          </label>
          <button
            style="float: right"
            class="btn btn-link inline"
            @click="
              () => {
                showRecoveryCodeInput = false;
                error = null;
                credentials.totpCode = '';
                credentials.totpRecoveryCode = '';
                mfaMode = null;
              }
            ">
            Back
          </button>
        </div>
      </div>

      <!-- Webauthn Mode -->
      <div
        class="fade-in-load-login"
        v-if="!isOneTimeLoggingIn && mfaMode === MFA_TYPES.WEBAUTHN">
        <div
          class="form-group"
          style="margin-top: 0.5em"
          :class="{
            'has-error': error || (loginInfoEntered && !validEmail),
          }">
          <div v-show="!showRecoveryCodeInput">
            <label class="form-label" for="totp-code">
              Use Your Authencation Device to Sign In
            </label>
            <div class="icon usb-key-icon-container">
              <font-awesome-icon class="fa-7x" :icon="faIcons.faUsbDrive" />
            </div>
            <div>Confirm your sign in with your authentication device now.</div>
          </div>
          <br />
          <transition name="fade">
            <div v-if="error">
              <p aria-live="assertive" class="form-input-hint">
                {{ formattedError }}
              </p>
            </div>
          </transition>
        </div>
        <div
          :class="{
            'button-box': !defaultSSOMode,
          }">
          <button
            class="btn btn-primary card-button"
            id="sign-in"
            :class="{ loading: isLoading }"
            @click="doLogin()"
            :disabled="isLoading">
            Sign In
          </button>
        </div>
        <div v-if="!QReserveReadOnlyMode && !emailProvidedToSSO">
          <label class="form-checkbox remember-me-switch">
            <input type="checkbox" v-model="saveSession" />
            <i class="form-icon"></i>
            Remember me
          </label>
          <button
            style="float: right"
            class="btn btn-link inline"
            @click="
              () => {
                error = null;
                credentials.webauthnCredentials = '';
                mfaMode = null;
              }
            ">
            Back
          </button>
        </div>
      </div>
    </div>
    <div class="terms-box">
      <a href="https://get.qreserve.com/legal/terms/" target="_blank">
        Terms of use
      </a>
      &nbsp;&nbsp;&middot;&nbsp;&nbsp;
      <a href="https://get.qreserve.com/legal/privacy/" target="_blank">
        Privacy policy
      </a>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

import { startAuthentication } from '@simplewebauthn/browser';

import * as api from 'assets/js/api';
import auth from 'assets/js/auth';
import SSO from 'assets/js/auth/sso';

import { HIDE_PARTNER_LOGIN } from 'assets/js/api/environment';

import Mixin from './Mixins';

export default {
  name: 'login-card',

  mixins: [Mixin],

  created() {
    this.setTitle('Login');

    if (this.themeName) {
      this.setTitle(`Sign in to ${this.themeName}`);
    }
  },

  computed: {
    // defaultSSOMode determines if the login card should be rendered
    // with the default SSO or not. Typically is the case when a default
    // SSO is stored and we aren't overriding it with anything.
    defaultSSOMode() {
      return this.defaultSSO && !this.emailOverride;
    },

    // errorIsVerification returns whether or not this is an email
    // verification issue.
    errorIsVerification() {
      if (
        _.includes(
          _.get(this.error, 'response.data.errors[0]'),
          'Email Verification Required',
        )
      ) {
        return true;
      }
      return false;
    },

    // formattedError returns a user-friendly string version of the login
    // error.
    formattedError() {
      if (!this.error) return '';
      if (
        (this.error.response &&
          _.includes([400, 401, 403], this.error.response.status)) ||
        (this.error.name &&
          _.includes(
            ['StatusCode400', 'StatusCode401', 'StatusCode403'],
            this.error.name,
          ))
      ) {
        if (this.QReserveSandboxMode) {
          return (
            'You are not authorized to access this server. Please ' +
            'contact QReserve support.'
          );
        } else {
          return 'Invalid credentials.';
        }
      }
      return String(this.error).replace('Error: ', '');
    },

    hidePartnerLogin() {
      return HIDE_PARTNER_LOGIN;
    },

    // loginInfoEntered checks to see if the user has entered something in
    // both the email and password fields.
    loginInfoEntered() {
      return this.credentials.email !== '' && this.credentials.password !== '';
    },

    logoutMessage() {
      return this.$store.state.auth.LogoutMessage;
    },

    // validEmail checks the email string to see if it formatted like an
    // email address.
    validEmail() {
      if (this.emailOverride) return true;
      if (_.isUndefined(this.credentials.email)) {
        return true;
      }
      if (this.credentials.email === '') {
        return true;
      }
      return this.emailRegex().test(this.credentials.email);
    },
  },

  data() {
    return {
      MFA_TYPES: {
        NONE: null,
        TOTP: 'totp',
        WEBAUTHN: 'webauthn',
      },
      credentials: {
        email: '',
        password: '',
        totpCode: '',
        totpRecoveryCode: '',
        webauthnCredentials: '',
      },
      defaultSSO: false,
      error: false,
      isLoading: false,
      isOneTimeLoggingIn: false,
      mfaMode: null,
      saveSession: false,
      showRecoveryCodeInput: false,
    };
  },

  emits: [
    'clearTheme',
    'done',
    'emitLoginWithDefaultSSO',
    'emitShowMembershipPortal',
  ],

  methods: {
    // doLogin connects to the server by the API and attempts to log in
    // using the given credentials. If the login is successful, it redirects
    // to the dashboard from within this function.
    async doLogin() {
      if (!this.credentials.email || !this.credentials.password) return;
      if (this.isLoading) return;
      this.isLoading = true;

      let mfaTypes = [];
      const credentials = {
        username: this.credentials.email,
        password: this.credentials.password,
      };

      // If we don't know the second factor yet, check and decide what to do.
      if (
        this.mfaMode === null &&
        this.credentials.totpCode === '' &&
        this.credentials.webauthnCredentials === ''
      ) {
        try {
          const resp2FA = await api.Post(
            api.urls.SECOND_FACTOR_CHECK,
            credentials,
          );
          mfaTypes = resp2FA.data.types;
          this.isLoading = false;
        } catch (error) {
          this.isLoading = false;
          this.error = error;
          this.mfaMode = null;
          return;
        }

        if (mfaTypes.length) {
          if (mfaTypes.length > 1) {
            try {
              await this.$swal({
                title: 'Please Choose An Authentication Method',
                input: 'radio',
                inputOptions: {
                  totp: 'Time-based code',
                  webauthn: 'Hardware key or passkey',
                },
                inputValue: 'totp',
                showCancelButton: false,
                confirmButtonText: 'Select',
                preConfirm: async (result) => {
                  this.mfaMode = result;
                },
              });
              return;
            } catch (err) {
              return;
            }
          } else {
            if (mfaTypes[0] === this.MFA_TYPES.TOTP) {
              this.mfaMode = this.MFA_TYPES.TOTP;
              return;
            }
            if (mfaTypes[0] === this.MFA_TYPES.WEBAUTHN) {
              this.mfaMode = this.MFA_TYPES.WEBAUTHN;
              return;
            }
            throw Error('Unknown MFA mode');
          }
        }
      }

      // Attempt to use recovery code to get TOTP and then issue a new recovery
      // code.
      if (this.credentials.totpRecoveryCode) {
        try {
          const resp = await api.Post(
            api.urls.USER_TOTP_USE_AND_EXPIRE_RECOVERY_CODE(
              this.credentials.email,
            ),
            {
              recovery_code: this.credentials.totpRecoveryCode,
            },
          );
          this.credentials.totpCode = resp.data.totp_code;
          this.$store.dispatch('SetTOTPRecoveryCode', resp.data.recovery_code);
        } catch (err) {
          this.error = Error('Bad recovery code.');
          this.isLoading = false;
          return;
        }
      }

      let expires = null;
      if (this.credentials.totpCode) {
        credentials.totpCode = this.credentials.totpCode.replace(/\s+/g, '');
      }
      if (this.credentials.webauthnCredentials) {
        credentials.webauthnCredentials = this.credentials.webauthnCredentials;
      }

      this.isLoading = true;

      if (
        this.saveSession ||
        Boolean(_.get(this, '$route.query.stay_logged_in', false))
      ) {
        expires = 90 * 24 * 60; // 90d in minutes
      }

      let membershipId;
      if (
        this.$route.query.idp_membership &&
        this.$route.query.need_association
      ) {
        membershipId = this.$route.query.idp_membership;
      }

      try {
        await auth.Login(credentials, expires, membershipId);
        if (this.saveSession) auth.StayLoggedInStore(true);
        auth.TokenStore({
          token: auth.GetAuthorizationToken(),
          expires: auth.GetTokenExpiration(),
        });

        this.isLoading = false;
        this.$emit('done');
      } catch (error) {
        this.isLoading = false;
        this.error = error;
      }
    },

    // doOneTimeLogin is a special login type specific to development/testing
    // where we do not verify email.
    doOneTimeLogin(token) {
      // If a special One Time Use token is provdied, login with that token.
      this.isOneTimeLoggingIn = true;
      auth
        .LoginOneTime(token)
        .then(() => {
          if (this.saveSession) auth.StayLoggedInStore(true);
          auth.TokenStore({
            token: auth.GetAuthorizationToken(),
            expires: auth.GetTokenExpiration(),
          });
          this.isLoading = false;
          this.$emit('done');
        })
        .catch((error) => {
          this.isLoading = false;
          this.isOneTimeLoggingIn = false;
          this.error = error;
        });
    },

    resetDefaultSSO() {
      try {
        window.localStorage.removeItem(SSO.DefaultEntityKey);
        this.defaultSSO = false;
      } catch (err) {
        // pass
      }
    },

    // webauthnAuthentication is the handler for webauthn MFA flows. It calls
    // the API which sends a challenge, which the device must then sign and
    // pass back to our API.
    async webauthnAuthentication() {
      // Get the challenge from our API.
      let challenge;
      try {
        const resp = await api.Post(api.urls.WEBAUTHN_AUTH_CHALLENGE, {
          username: this.credentials.email,
          password: this.credentials.password,
        });
        challenge = resp.data.challenge;
      } catch (err) {
        this.error = err;
        return;
      }
      let response;
      try {
        // Pass the options to the authenticator and wait for a response
        response = await startAuthentication(JSON.parse(challenge));
      } catch (err) {
        this.error = err;
        return;
      }

      this.credentials.webauthnCredentials = JSON.stringify(response);
      this.doLogin();
    },
  },

  mounted() {
    if (this.emailOverride) this.credentials.email = this.emailOverride;
    if (this.$route.query.onetime) {
      this.doOneTimeLogin(this.$route.query.onetime);
    }

    // Attempt to pull in our default SSO
    let defaultSSO = false;
    try {
      defaultSSO = window.localStorage.getItem(SSO.DefaultEntityKey);
    } catch (err) {
      // pass
    }

    if (defaultSSO) {
      try {
        this.defaultSSO = JSON.parse(defaultSSO);
      } catch (err) {
        // LocalStorage is corrupt so remove it
        this.resetDefaultSSO();
      }
    }
  },

  props: {
    // Functions
    goToPasswordReset: {
      type: Function,
      required: true,
    },
    goToSignup: {
      type: Function,
      required: true,
    },

    // Objects
    emailProvidedToSSO: {
      required: true,
    },
    logoStyle: {
      type: Object,
      required: false,
      default: () => {},
    },

    // Strings
    emailOverride: {
      type: String,
    },
    logoLoginUrl: {
      type: String,
      required: false,
    },
    themeName: {
      type: String,
      required: false,
    },
    topMessage: {
      type: String,
    },

    // Boolean
    chromeExtension: {
      type: Boolean,
    },
    darkMode: {
      type: Boolean,
    },
    outlookAddin: {
      type: Boolean,
    },
  },

  watch: {
    emailOverride: {
      handler: async function handle(eo) {
        if (eo) {
          this.credentials.email = eo;
        }
      },
    },
    mfaMode: {
      handler: function handle(val, oldVal) {
        if (val === this.MFA_TYPES.WEBAUTHN && val !== oldVal) {
          this.webauthnAuthentication();
        }
      },
    },
    saveSession: {
      handler: function handle(val) {
        auth.StayLoggedInStore(val);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~assets/css/colors';
@import '~assets/css/partials/login';

.card-box {
  .default-sso {
    text-align: center;
    margin-top: 2em;
    span {
      display: block;
      color: #444;
    }
    .logo {
      margin-bottom: 10px;
      max-height: 60px;
      img {
        height: 100%;
        width: auto;
      }
    }
    .name {
      font-size: larger;
      font-weight: bold;
    }
  }
}

.dark-mode {
  .card-box {
    .default-sso {
      span {
        color: $dmtextlight;
      }
    }
  }
}

.btn-default-sso {
  margin-top: 0.6em;
}

.remember-me-switch {
  font-size: 1.1em;
  padding-right: 0;
}

.powered {
  position: absolute;
  top: -4px;
  right: 8px;
  img {
    height: 8px;
  }
}

.hint-success {
  color: $successcolor;
}

span.form-icon {
  position: absolute;
  margin-top: 6px;
  margin-left: 8px;
  z-index: 10;
  opacity: 0.4;
}

span.form-icon-totp {
  position: absolute;
  margin-top: 42px;
  margin-left: 14px;
  z-index: 10;
  opacity: 0.4;
  transform: scale(1.75);
}

.form-icon-offset {
  padding: 12px 28px;
}

.button-box-sso-remember-indent {
  padding-left: 3rem;
  padding-top: 1rem;
}

.outlookAddin {
  border: none;
  box-shadow: none;
}

.theme-banner {
  padding: 0.2em;
  margin-top: 1.2em;
  font-size: smaller;
  margin-left: -20px;
  margin-right: -20px;
  text-align: center;

  .btn-link {
    padding: 0;
    display: inline;
    height: 1.2em;
    line-height: 1.2em;
  }
}

.has-error .form-input-hint {
  color: $dangercolor;
}

.totp-input-box {
  width: 100%;
  padding: 4rem;
  font-size: 4rem;
  box-sizing: border-box;
  margin: 10px 0;
}

.usb-key-icon-container {
  width: 100%;
  height: 12rem;
  text-align: center;
  padding-top: 2rem;
}
</style>
